import React, { useEffect, useRef, useState } from "react";
import {
  ArrowRightIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import Swal from "sweetalert2";
import Toaster from "@/components/Toaster";
import {
  clearUser,
  logout,
  togglePushNotification,
} from "@/store/slices/userSlice";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { requestAccountDeletion, updateDeliveryDP, getLoginUserData } from "@/store/slices/newDeliverySlice";
import ShowLoader from "@/components/Loader";
import DeliveryLanguageSubMenu from "@/widgets/layout/DeliveryLanguageSubMenu";
import { useTranslation } from "react-i18next";


export default function ProfileDrawer({ isOpen, onClose }) {
  const { t } = useTranslation("delivery");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, loading: userLoading } = useSelector((state) => state.user);
  const { loading, profile_data } = useSelector((state) => state.delivery);
  
  const [isNotificationEnabled, setIsNotificationEnabled] = useState(true);  
  const fileInputRef = useRef(null);

  // Fetch profile data when drawer opens
  useEffect(() => {
    dispatch(getLoginUserData());
  }, []);

  useEffect(() => {
    if (user) {
      setIsNotificationEnabled(user?.push_notification == 1);
    }
  }, [user]);

  const handleImageUpload = (file) => {
    if (!file) return;

    const allowedTypes = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
    const maxSizeInBytes = 20 * 1024 * 1024; // 20MB

    if (!allowedTypes.includes(file.type)) {
      Toaster.error(t("invalid_file_type_image"));
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    if (file.size > maxSizeInBytes) {
      Toaster.error(t("file_too_large_20mb"));
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    dispatch(updateDeliveryDP(file))
      .unwrap()
      .then((res) => {
        if (res?.profile_image || res?.status === true) {
          Toaster.success(res?.message || t("profile_image_update_success"));
          dispatch(getLoginUserData());
        } else {
          Toaster.error(res?.message || t("profile_image_update_failed"));
        }
      })
      .catch((err) => {
        const errorMsg = err?.message || "An error occurred during upload.";
        Toaster.error(errorMsg);
        console.error("Upload Error:", err);
      });
  };

  const openCamera = async () => {
    Swal.fire({
      title: t("take_a_photo"),
      showCloseButton: true,
      html: `
        <div class="flex flex-col items-center p-2">
            <div class="relative w-full aspect-video bg-black rounded-xl overflow-hidden border-2 border-gray-200 shadow-inner">
                <video id="webcam" autoplay playsinline class="w-full h-full object-cover"></video>
                <div class="absolute inset-0 pointer-events-none border-[16px] border-black/10"></div>
            </div>
            <canvas id="photoCanvas" class="hidden"></canvas>
            <p class="text-xs text-gray-500 mt-3">${t("center_your_face")}</p>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: t("capture_photo"),
      cancelButtonText: t("cancel"),
      buttonsStyling: false,
      customClass: {
        popup: "rounded-2xl border-none shadow-2xl",
        title: "text-xl font-bold text-gray-800 pt-6",
        confirmButton: "bg-[#065C36] text-white px-8 py-2.5 rounded-lg font-semibold hover:bg-[#044a2b] transition-all mx-2 shadow-md",
        cancelButton: "bg-gray-100 text-gray-600 px-8 py-2.5 rounded-lg font-semibold hover:bg-gray-200 transition-all mx-2"
      },
      willOpen: async () => {
        const video = document.getElementById("webcam");
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ 
            video: { facingMode: "user", width: 1280, height: 720 } 
          });
          video.srcObject = stream;
          window.localStream = stream;
        } catch (err) {
          Toaster.error(t("camera_access_denied"));
          Swal.close();
        }
      },
      preConfirm: () => {
        const video = document.getElementById("webcam");
        const canvas = document.getElementById("photoCanvas");
        if (!video || !canvas) return null;

        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        canvas.getContext("2d").drawImage(video, 0, 0);

        return new Promise((resolve) => {
          canvas.toBlob((blob) => {
            const file = new File([blob], "webcam_capture.jpg", { type: "image/jpeg" });
            resolve(file);
          }, "image/jpeg");
        });
      },
      didClose: () => {
        if (window.localStream) {
          window.localStream.getTracks().forEach((track) => track.stop());
        }
      },
    }).then((result) => {
      if (result.isConfirmed && result.value) {
        handleImageUpload(result.value);
      }
    });
  };

  const handleCameraClick = () => {
    Swal.fire({
      title: t("update_profile_picture"),
      text: t("how_to_update_photo"),
      icon: "question",
      iconColor: "#F4B11F",
      showCloseButton: true,
      showDenyButton: true,
      showCancelButton: false, 
      confirmButtonText: t("upload_from_device"),
      denyButtonText: t("use_camera"),
      buttonsStyling: false,
      customClass: {
        popup: "rounded-2xl p-4",
        title: "text-lg font-bold text-gray-800",
        htmlContainer: "text-gray-600",
        closeButton: "absolute top-2 right-2 w-8 h-8 text-gray-400 hover:text-red-500 transition-colors focus:shadow-none border-none scale-75",
        confirmButton: "bg-[#F4B11F] text-white px-6 py-3 rounded-xl font-bold w-[200px] mb-2 hover:opacity-90 transition shadow-sm",
        denyButton: "bg-[#065C36] text-white px-6 py-3 rounded-xl font-bold w-[200px] mb-2 hover:opacity-90 transition shadow-sm",
        actions: "flex flex-col items-center gap-2 pt-4" 
      },
    }).then((result) => {
      if (result.isConfirmed) {
        fileInputRef.current.click();
      } else if (result.isDenied) {
        openCamera();
      }
    });
  };

  const handleLogout = () => {
    Swal.fire({
      title: t("are_you_sure"),
      text: t("logout_confirmation_text"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: t("yes_logout"),
      cancelButtonText: t("cancel")
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(logout())
          .unwrap()
          .then(() => {
            Toaster.success(t("logout_success"));
            localStorage.clear();
            navigate("/sign-in");
            setTimeout(() => {
              dispatch(clearUser());
            }, 200);
          })
          .catch((error) => {
            console.error("Logout failed:", error); // This is a console log, no need to translate
            Toaster.error(t("logout_failed"));
          });
      }
    });
  };

  const handleAccountDeletion = () => {
    Swal.fire({
      title: t("request_account_deletion"),
      text: t("request_deletion_confirmation"),
      imageUrl: "/img/icons/Group_8886.png",
      imageWidth: 60,
      imageHeight: 60,
      showCancelButton: true,
      confirmButtonText: t("request"),
      cancelButtonText: t("back"),
      reverseButtons: true,
      buttonsStyling: false,
      customClass: {
        popup: "rounded-xl px-6 py-6",
        title: "text-lg font-semibold text-gray-800",
        actions: "flex justify-center gap-4 mt-5",
        confirmButton: "bg-red-500 text-white px-6 py-2 rounded-md hover:bg-red-600 transition",
        cancelButton: "text-amber-500 px-4 py-2 bg-transparent border-0 shadow-none hover:underline",
      },
    }).then((result) => {
      if (!result.isConfirmed) return;

      dispatch(requestAccountDeletion())
        .unwrap()
        .then((res) => {
          if (res?.status) {
            Toaster.success(res?.message);
            onClose();
          } else {
            Toaster.error(res?.message || t("something_went_wrong"));
            onClose();
          }
        })
        .catch((err) => {
          Toaster.error(err?.message || t("failed_to_send_request"));
        });
    });
  };

  const handleToggleNotification = () => {
    const newValue = isNotificationEnabled ? 0 : 1;
    setIsNotificationEnabled(!isNotificationEnabled);

    dispatch(togglePushNotification(newValue))
      .unwrap()
      .then((res) => {
        if (res?.status) {
          Toaster.success(res?.message);
        } else {
          throw new Error(res?.message);
        }
      })
      .catch((err) => {
        setIsNotificationEnabled(isNotificationEnabled);
        Toaster.error(err?.message || t("something_went_wrong"));
      });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {(loading || userLoading) && <ShowLoader />}
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        accept="image/*"
        onChange={(e) => handleImageUpload(e.target.files[0])}
      />

      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="relative w-full max-w-md bg-white shadow-2xl h-full animate-in slide-in-from-right duration-300">
        {/* Header */}
        <div className="relative flex items-center gap-3 px-8 py-6 bg-[#F5F5F5] border-b border-gray-300">
          <img
            src="/img/logos/logo.png"
            alt="Corporate Supplier"
            className="h-10"
          />
          <div className="flex flex-col leading-tight">
            <span className="text-lg md:text-xl font-medium text-gray-800 tracking-tight capitalize">
              {t("corporate")}
            </span>
            <span className="text-lg md:text-xl font-medium text-gray-800 tracking-tight capitalize">
              {t("supplier")}
            </span>
          </div>

          <button
            onClick={onClose}
            className="absolute right-6 top-1/2 -translate-y-1/2 p-2 rounded-full hover:bg-gray-200 transition-colors group"
          >
            <XMarkIcon className="h-6 w-6 text-gray-500 group-hover:text-red-500" />
          </button>
        </div>

        {/* Profile Section */}
        <div className="bg-[#EEF2F7] px-8 py-6 flex items-center gap-4">
          <div className="relative w-24 h-24">
            <img
              src={profile_data?.profile_image || "/img/photo-gallery.png"}
              alt="Profile"
              className="w-full h-full rounded-full object-cover border-4 border-[#F4B11F61] shadow-md"
            />

            <button
              type="button"
              onClick={handleCameraClick}
              className="absolute bottom-1 right-1 w-8 h-8 flex items-center justify-center rounded-full bg-white border border-[#F4B11F61] shadow-md hover:bg-[#F4B11F] transition-all duration-300 group"
            >
              <img
                src="/img/icons/Container-camera.png"
                alt="Edit"
                className="w-4 h-4 group-hover:brightness-0 group-hover:invert transition"
              />
            </button>
          </div>

          <div>
            <h3 className="font-semibold text-lg text-gray-900">
              {profile_data?.full_name || "User"}
            </h3>
            <p className="text-sm text-gray-800">{profile_data?.email}</p>
            <p className="text-sm text-gray-800">{profile_data?.mobile_number}</p>
          </div>
        </div>

        {/* Body */}
        <div className="px-8 py-6 space-y-6">
          {/* Edit Profile */}
          <div
            onClick={() => {
              onClose();
              navigate("/delivery-partner/edit-profile");
            }}
            className="flex items-center justify-between cursor-pointer group"
          >
            <span className="text-gray-900 font-medium group-hover:text-amber-500 transition underline capitalize">
              {t("edit_profile_info")}
            </span>
            <ArrowRightIcon className="h-5 w-5 text-gray-800 group-hover:text-amber-500 transition" />
          </div>

          {/* Reused Language Submenu */}
          <div className="w-full">
            <DeliveryLanguageSubMenu />
          </div>

          {/* Push Notification */}
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900 capitalize">{t("push_notifications")}</p>
              <p className="text-xs text-gray-500 capitalize">
                {t("receive_delivery_updates")}
              </p>
            </div>

            <button
              onClick={handleToggleNotification}
              className={`relative w-11 h-6 rounded-full transition ${
                isNotificationEnabled ? "bg-amber-400" : "bg-gray-300"
              }`}
            >
              <span
                className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition ${
                  isNotificationEnabled ? "left-6" : "left-1"
                }`}
              />
            </button>
          </div>

          <div className="pt-20 border-t border-gray-100 space-y-4">
            {/* Delete */}
            <div
              onClick={handleAccountDeletion}
              className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition"
            >
              <TrashIcon className="h-5 w-5 text-red-500" />
              <span className="text-gray-900 capitalize">{t("request_account_deletion")}</span>
            </div>

            {/* Logout */}
            <div
              className="flex items-center gap-3 cursor-pointer text-amber-500 hover:opacity-80 transition"
              onClick={handleLogout}
            >
              <img 
                src="/img/icons/logout.png" 
                alt="Logout" 
                className="h-5 w-5 object-contain" 
              />
              <span className="capitalize">{t("logout")}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}